import {
  CalendarIcon,
  DatePicker,
  DatePickerProps,
  DatePickerSlotsComponents,
  DatePickerSlotsComponentsProps,
} from '@mui/x-date-pickers-pro';
import { forwardRef } from 'react';

import { dateTimeToDate, dateToDateTime } from 'helpers/datetime';

import TimezoneTextField from './components/TimezoneTextField';

import type { TextFieldProps } from '@mui/material';
import type { DateTime } from 'luxon';

type OmittedDatePickerProps =
  | 'components'
  | 'componentsProps'
  | 'localeText'
  | 'slotProps'
  | 'slots'
  | 'viewRenderers';

type PickedDatePickerSlotsComponentsProps = 'inputAdornment' | 'field';

type PickedDatePickerSlotsComponents = 'OpenPickerIcon';

interface LocatedDatePickerProps extends Omit<
  DatePickerProps<Date>,
  OmittedDatePickerProps
> {
  placeholder?: string;
  showCurrentTimeZone?: boolean;
  slotProps?: Pick<
    DatePickerSlotsComponentsProps<Date>,
    PickedDatePickerSlotsComponentsProps
  >;
  slots?: Pick<
    DatePickerSlotsComponents<Date>,
    PickedDatePickerSlotsComponents
  >;
  TextFieldProps?: TextFieldProps;
  timezone?: string;
}

const LocatedDatePicker = forwardRef<HTMLDivElement, LocatedDatePickerProps>(
  (
    {
      placeholder,
      showCurrentTimeZone = true,
      slotProps,
      slots,
      TextFieldProps,
      timezone,
      ...props
    }: LocatedDatePickerProps,
    ref,
  ) => {
    /**
     * Convert between Date and DateTime props due to the usage of Luxon DateTime in the
     * MUI DatePicker component
     */
    const convertedDatePickerProps: Omit<
      DatePickerProps<DateTime>,
      OmittedDatePickerProps
    > = {
      ...props,
      dayOfWeekFormatter:
        props.dayOfWeekFormatter &&
        ((day, date) => props.dayOfWeekFormatter!(day, dateTimeToDate(date))),
      defaultCalendarMonth:
        props.defaultCalendarMonth &&
        dateToDateTime(props.defaultCalendarMonth, { timezone }),
      defaultValue:
        props.defaultValue && dateToDateTime(props.defaultValue, { timezone }),
      maxDate: props.maxDate && dateToDateTime(props.maxDate, { timezone }),
      minDate: props.minDate && dateToDateTime(props.minDate, { timezone }),
      onAccept:
        props.onAccept &&
        ((value) => props.onAccept!(value ? dateTimeToDate(value) : null)),
      onChange:
        props.onChange &&
        ((value, context) =>
          props.onChange!(value ? dateTimeToDate(value) : null, context)),
      onError:
        props.onError &&
        ((error, value) =>
          props.onError!(error, value ? dateTimeToDate(value) : null)),
      onMonthChange:
        props.onMonthChange &&
        ((month) => props.onMonthChange!(dateTimeToDate(month))),
      onYearChange:
        props.onYearChange &&
        ((year) => props.onYearChange!(dateTimeToDate(year))),
      referenceDate:
        props.referenceDate &&
        dateToDateTime(props.referenceDate, { timezone }),
      shouldDisableDate:
        props.shouldDisableDate &&
        ((day) => props.shouldDisableDate!(dateTimeToDate(day))),
      shouldDisableMonth:
        props.shouldDisableMonth &&
        ((month) => props.shouldDisableMonth!(dateTimeToDate(month))),
      shouldDisableYear:
        props.shouldDisableYear &&
        ((year) => props.shouldDisableYear!(dateTimeToDate(year))),
      timezone,
      value: props.value ? dateToDateTime(props.value, { timezone }) : null,
    };

    return (
      <DatePicker<DateTime>
        {...convertedDatePickerProps}
        ref={ref}
        // Type cast slotProps and slots to work around MUI X typing issue.
        // See: https://github.com/mui/mui-x/issues/9775
        slotProps={
          {
            ...slotProps,
            textField: {
              placeholderProp: placeholder,
              showCurrentTimeZone,
              timezoneProp: timezone,
              ...TextFieldProps,
            } as any,
          } as DatePickerSlotsComponentsProps<DateTime>
        }
        slots={{
          ...slots,
          openPickerIcon: slots?.OpenPickerIcon ?? CalendarIcon,
          textField: TimezoneTextField as any,
        }}
      />
    );
  },
);

export default LocatedDatePicker;
